import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Phone, Clock, Navigation } from 'lucide-react';
import { useSiteSettings } from '@/hooks/useSiteSettings';

const Locations = () => {
  const { t } = useLanguage();
  const { contactInfo, hours } = useSiteSettings();

  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.address)}`;

  return (
    <main>
      {/* Hero */}
      <section className="bg-hero-gradient text-white py-12 md:py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-['Playfair_Display'] mb-3 md:mb-4">
            {t('locationsTitle')}
          </h1>
          <p className="text-base md:text-xl text-white/90">
            Find us in the heart of Carrollton, GA
          </p>
        </div>
      </section>

      {/* Location Info */}
      <section className="py-8 md:py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Info Card */}
            <Card className="p-4 md:p-8">
              <h2 className="text-xl md:text-3xl font-bold font-['Playfair_Display'] mb-4 md:mb-8 text-primary">
                Junnior's Tacos Mexican Restaurant
              </h2>

              {/* Address */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-start mb-2">
                  <MapPin className="w-5 h-5 md:w-6 md:h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm md:text-lg mb-1">{t('address')}</h3>
                    <p className="text-xs md:text-base text-muted-foreground">
                      {contactInfo.address}
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-start mb-2">
                  <Phone className="w-5 h-5 md:w-6 md:h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm md:text-lg mb-1">{t('phone')}</h3>
                    <a
                      href={`tel:${contactInfo.phone.replace(/[^0-9+]/g, '')}`}
                      className="text-xs md:text-base text-muted-foreground hover:text-primary transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-start mb-2">
                  <Clock className="w-5 h-5 md:w-6 md:h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm md:text-lg mb-3">{t('hours')}</h3>
                    <div className="space-y-2 text-xs md:text-base text-muted-foreground">
                      <div className="flex justify-between">
                        <span>{t('mondayThursday')}</span>
                        <span className="ml-4">{hours.monday_thursday}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('fridaySaturday')}</span>
                        <span className="ml-4">{hours.friday_saturday}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('sunday')}</span>
                        <span className="ml-4">{hours.sunday}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button asChild className="flex-1 text-sm md:text-base">
                  <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                    <Navigation className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                    {t('getDirections')}
                  </a>
                </Button>
                <Button variant="secondary" asChild className="flex-1 text-sm md:text-base">
                  <a href={`tel:${contactInfo.phone.replace(/[^0-9+]/g, '')}`}>
                    <Phone className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                    {t('callNow')}
                  </a>
                </Button>
              </div>
            </Card>

            {/* Map */}
            <Card className="p-0 overflow-hidden">
              <iframe
                src="https://www.google.com/maps?q=727+Bankhead+Hwy,+Carrollton,+GA+30117&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Junnior's Tacos Location"
              />
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Locations;
